/* eslint-disable max-classes-per-file */
/* eslint-disable class-methods-use-this */
import { PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import {
  inspection,
  getFocusedDifferenceId,
  getGroupOrDifferenceId,
  getGroupedDifferences,
  getDifferences,
  getUnfilteredDifferences,
  getShowDifferenceBoxes,
  getSelectedTool,
} from 'store';
import store from 'store/store';
import {
  Difference as DifferenceType,
  DocumentTypes,
  DifferenceTypeNames,
  DocumentType,
  AnnotationCustomData,
  AnnotationVisibilityOptions,
  DiffToAnnotData,
  ContentDifferences,
  FormattingDifferences,
  SpellingDifferences,
  MarkupZoneCustomData,
  ZoomLevelLimits,
  AnnotationData,
} from 'types';
import { PDFTronTools } from 'types';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { isReportsUrl, isResultUrl } from 'utils/location';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import Utils from 'components/PDFViewer/Utils';
import GraphicZone from './AnnotationTools/GraphicZone';
import GVAnnotationMixin from './AnnotationTools/utils/GVAnnotationMixin';
import {
  COLOR_POINT_ANNOTATION_SIZE,
  PREP_TOOL_LABEL,
  PREP_TOOL_ANNOTATION,
} from './AnnotationTools/utils/annotationConstants';
import drawMarkupLabel from './AnnotationTools/utils/drawMarkupLabel';
import { getGroupLabelFromStore } from './AnnotationTools/utils/getGroupLabelFromStore';
import { getPageRotation } from './AnnotationTools/utils/labelPositionUtils';
import { updatePreviousScrollPostion } from 'components/common/GVDocumentsTopBar/SyncScrollingButton';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { useDifferenceListStore } from 'zstore/differenceListStore';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { DEFAULT_DIFFERENCE_COLOR, differenceHighlightColors } from './differenceColors';
import { ColorId, colorIdToHex } from '@verify/shared-utils';
import { FlashAnimationManager } from './AnnotationTools/utils/FlashAnimationManager';
import { getBarcodeGradeColor } from 'pdftron/utils/barcodeGradeColors';
import { getCachedFlagValue } from 'utils/getFlagValue/getFlagValue';

/** Label sits above the hitbox; tiny Color rects otherwise hit differenceMax zoom and clip the label */
const COLOR_DIFFERENCE_ZOOM_EXTRA_PADDING_PX = 40;
const COLOR_DIFFERENCE_ZOOM_UPPER_BOUND = 6;

/**
 * Computes annotation bounds from a rect [x, y, w, h], applying growth padding
 * and normalizing negative dimensions caused by rotated content (90°/180°).
 */
export function normalizeAnnotationRect(rect: number[], growth: number) {
  let [x, y, w, h] = rect;
  // Normalize negative dimensions first, then apply growth.
  if (w < 0) {
    x += w;
    w = -w;
  }
  if (h < 0) {
    y += h;
    h = -h;
  }
  return { x: x - growth, y: y - growth, w: w + 2 * growth, h: h + 2 * growth };
}

function focusDiffListScroller(instance: WebViewerInstance | null | undefined) {
  if (!instance) return;
  const scroller = document.getElementById('diff-list-scroller');
  if (scroller) {
    instance.UI.iframeWindow?.document.body.blur();
    scroller.focus();
    scroller.click();
  }
}

class Difference {
  public source: WebViewerWrapper | null = null;

  public target: WebViewerWrapper | null = null;

  get graphic(): GraphicZone {
    return new GraphicZone(this.source, this.target);
  }

  differenceAnnotationSelected(annotation: Core.Annotations.Annotation, documentType: DocumentTypes) {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();
    if (!pdfDocManager) return;

    // Check if this is a markup annotation
    const isMarkupAnnotation = annotation.getCustomData('markupZoneAnnotation') === 'true';

    // select the matching difference annotation on other document
    const oppositeDocument = documentType === DocumentTypes.source ? DocumentTypes.target : DocumentTypes.source;
    const matchingDifferenceAnnot = this[oppositeDocument]?.getAnnotationById(annotation.Id);
    const selectedAnnotations = this[oppositeDocument]?.instance.Core.annotationManager.getSelectedAnnotations();
    const isAlreadySelected = selectedAnnotations?.some((annot) => annot.Id === annotation.Id);

    // For markup annotations, only select opposite annotation if it's linked (comparison mode)
    // For regular differences, use the existing logic
    let shouldSelectOpposite = false;
    if (isMarkupAnnotation) {
      // Only select opposite annotation if this markup has a linked markup ID (comparison mode)
      shouldSelectOpposite = !!matchingDifferenceAnnot;
    } else {
      // Regular difference behavior - select opposite if it exists
      shouldSelectOpposite = !!matchingDifferenceAnnot;
    }

    // check if its already selected to prevent infinite loop
    if (!isAlreadySelected && shouldSelectOpposite && matchingDifferenceAnnot) {
      this[oppositeDocument]?.instance.Core.annotationManager.selectAnnotation(matchingDifferenceAnnot);
      // fix for grouped annotation popup ui not working when clicking on the outside VE-8395
      if (annotation.getCustomData('groupId') && documentType === DocumentTypes.target) {
        setTimeout(() => {
          this[documentType]?.instance.Core.annotationManager.selectAnnotation(annotation);
        }, 50);
      }
    }

    // If resync tool is active, do not focus/zoom differences on click
    const selectedTool = getSelectedTool(store.getState());
    if (selectedTool === PDFTronTools.RESYNC) {
      return;
    }

    if (documentType === DocumentTypes.source) {
      if (annotation.getCustomData(AnnotationCustomData.difference) === 'true') {
        focusDiffListScroller(this.source?.instance);
      }
      return; // keep logic below for only target document since we always select both differences
    }

    const state = store.getState();
    // get difference id or group id if exists
    const groupOrDifference = getGroupOrDifferenceId(state, annotation.Id);
    const differencesInGroup = getGroupedDifferences(groupOrDifference)(state);
    const focusedDifference = differencesInGroup.find((diff) => diff.id === annotation.Id);

    if (!focusedDifference) return;

    const prevDifferenceId = getFocusedDifferenceId(state);
    // focus the new difference if it wasn't focused already
    if (prevDifferenceId !== focusedDifference.id) {
      // The saga will handle zoom and scroll after overlays are created
      store.dispatch(inspection.actions.focusDifference(focusedDifference.id));
    }
    if (annotation.getCustomData(AnnotationCustomData.difference) === 'true') {
      focusDiffListScroller(this.target?.instance);
    }
  }

  deselectDifference(differences: DifferenceType[]) {
    if (!differences.length) return;

    // reset any existing graphic animation
    FlashAnimationManager.stopAllAnimations(this.source, this.target);

    // remove existing toast
    useDocumentToastStateStore.getState().expireAllToast();

    // remove existing highlight
    this.target?.removeAnnotationDocumentHighlight();
  }

  /**
   * Draws the high-contrast dimming overlay for a (possibly grouped) difference.
   *
   * @param options.pages         Restrict drawing to these page numbers (lazy per-visible-page draw).
   * @param options.documentType  Restrict drawing to a single side (used by the on-scroll lazy path,
   *                              which fires per document viewer).
   * @param options.skipIfPresent Skip pages that already have a matching overlay (no refresh churn).
   */
  applyHighContrast(
    differences: DifferenceType[],
    options?: { pages?: number[]; documentType?: DocumentTypes; skipIfPresent?: boolean },
  ): void {
    // No high contrast for global font results
    if (differences[0].fontMetricsDetails?.isGlobalFontResult) {
      return;
    }

    const overlayOptions = { pages: options?.pages, skipIfPresent: options?.skipIfPresent };
    // When a single side is requested (on-scroll lazy path), only touch that side.
    const drawSource = options?.documentType !== DocumentTypes.target;
    const drawTarget = options?.documentType !== DocumentTypes.source;

    // Special handling for Markup differences
    if (differences[0].type === DifferenceTypeNames.Markup) {
      // Check if markup has annotations on both source and target
      const sourceAnnotation = this.source?.getAnnotationById(differences[0].id);
      const targetAnnotation = this.target?.getAnnotationById(differences[0].id);

      // Apply high contrast to whichever side(s) have the markup annotation
      if (sourceAnnotation && drawSource) {
        this.source?.highContrastAnnotations(differences, overlayOptions);
      }
      if (targetAnnotation && drawTarget) {
        this.target?.highContrastAnnotations(differences, overlayOptions);
      }
      return;
    }

    // Color sample markers are target-only; high-contrast overlay should not run on source
    if (differences[0].type === DifferenceTypeNames.Color) {
      if (drawTarget) this.target?.highContrastAnnotations(differences, overlayOptions);
      return;
    }

    if (
      drawSource &&
      (differences[0].type === DifferenceTypeNames.Graphics ||
        differences[0].type === DifferenceTypeNames.Barcode ||
        differences[0].type === DifferenceTypeNames.EmbeddedImage ||
        differences[0].type === DifferenceTypeNames.FontMetrics ||
        differences[0].type === DifferenceTypeNames.UnmatchedAnnotation ||
        (Difference.hasGrid(differences, DocumentTypes.source) && !Difference.isSpelling(differences)))
    ) {
      this.source?.highContrastAnnotations(differences, overlayOptions);
    }
    // Skip overlay creation for Deletion differences only - they use MissingContentMarkerAnnotation on target
    // Insertions still need normal high contrast on target (where the content exists)
    if (drawTarget && differences[0].type !== DifferenceTypeNames.Deletion) {
      this.target?.highContrastAnnotations(differences, overlayOptions);
    }
  }

  /**
   * On-scroll lazy overlay drawing. Called from the viewer's visiblePagesChanged listener: draws the
   * focused group's high-contrast overlay for any newly-visible pages that don't have one yet.
   * No-ops when nothing is focused, so it's safe to call on every scroll tick.
   */
  drawVisibleGroupHighContrast(documentType: DocumentTypes, visiblePages: number[]): void {
    if (!visiblePages?.length) return;
    const state = store.getState();
    const focusedDifferenceId = getFocusedDifferenceId(state);
    if (!focusedDifferenceId) return;

    const groupOrDifference = getGroupOrDifferenceId(state, focusedDifferenceId);
    const group = getGroupedDifferences(groupOrDifference)(state);
    if (!group.length) return;

    this.applyHighContrast(group, { pages: visiblePages, documentType, skipIfPresent: true });
  }

  manageDeletionMarkerVisibility(differences: DifferenceType[]): void {
    // Get the actually focused difference from the store, not just any difference in the group
    const state = store.getState();
    const focusedDifferenceId = getFocusedDifferenceId(state);
    const focusedDifference = differences.find((diff) => diff.id === focusedDifferenceId) || differences[0];

    const diffIds = new Set(differences.map((diff) => diff.id));

    // Handle deletion markers on target document
    if (this.target) {
      const targetAnnotManager = this.target.annotationManager;
      if (targetAnnotManager) {
        const allTargetAnnotations = targetAnnotManager.getAnnotationsList();
        const deletionMarkers = allTargetAnnotations.filter(
          (annot) => annot.getCustomData('differenceType') === DifferenceTypeNames.Deletion,
        );

        // Hide all non-focused markers to ensure only the currently selected marker is visible
        const markersInGroup = deletionMarkers.filter((marker) => diffIds.has(marker.Id));
        const markersOutsideGroup = deletionMarkers.filter((marker) => !diffIds.has(marker.Id));

        if (markersOutsideGroup.length > 0) {
          targetAnnotManager.hideAnnotations(markersOutsideGroup);
        }

        // Show all markers in the group but only select the focused one
        if (markersInGroup.length > 0) {
          targetAnnotManager.showAnnotations(markersInGroup);

          // Find the marker that matches the focused difference
          const focusedMarker = markersInGroup.find((marker) => marker.Id === focusedDifference.id);

          if (focusedMarker) {
            // Trigger redraw to update visibility
            targetAnnotManager.redrawAnnotation(focusedMarker);
            // Select the marker to show the blue selection rectangle
            targetAnnotManager.selectAnnotation(focusedMarker);
          }
        }
      }
    }

    // Handle insertion markers on source document
    if (this.source) {
      const sourceAnnotManager = this.source.annotationManager;
      if (sourceAnnotManager) {
        const allSourceAnnotations = sourceAnnotManager.getAnnotationsList();
        const insertionMarkers = allSourceAnnotations.filter(
          (annot) => annot.getCustomData('differenceType') === DifferenceTypeNames.Insertion,
        );

        // Hide all non-focused markers to ensure only the currently selected marker is visible
        const markersInGroup = insertionMarkers.filter((marker) => diffIds.has(marker.Id));
        const markersOutsideGroup = insertionMarkers.filter((marker) => !diffIds.has(marker.Id));

        if (markersOutsideGroup.length > 0) {
          sourceAnnotManager.hideAnnotations(markersOutsideGroup);
        }

        // Show all markers in the group but only select the focused one
        if (markersInGroup.length > 0) {
          sourceAnnotManager.showAnnotations(markersInGroup);

          // Find the marker that matches the focused difference
          const focusedMarker = markersInGroup.find((marker) => marker.Id === focusedDifference.id);

          if (focusedMarker) {
            // Trigger redraw to update visibility
            sourceAnnotManager.redrawAnnotation(focusedMarker);
            // Select the marker to show the blue selection rectangle
            sourceAnnotManager.selectAnnotation(focusedMarker);
          }
        }
      }
    }
  }

  hideAllDeletionMarkers(): void {
    // Hide all deletion markers on target document
    if (this.target) {
      const targetAnnotManager = this.target.annotationManager;
      if (targetAnnotManager) {
        const allTargetAnnotations = targetAnnotManager.getAnnotationsList();
        const deletionMarkers = allTargetAnnotations.filter(
          (annot) => annot.getCustomData('differenceType') === DifferenceTypeNames.Deletion,
        );
        targetAnnotManager.hideAnnotations(deletionMarkers);
      }
    }

    // Hide all insertion markers on source document
    if (this.source) {
      const sourceAnnotManager = this.source.annotationManager;
      if (sourceAnnotManager) {
        const allSourceAnnotations = sourceAnnotManager.getAnnotationsList();
        const insertionMarkers = allSourceAnnotations.filter(
          (annot) => annot.getCustomData('differenceType') === DifferenceTypeNames.Insertion,
        );
        sourceAnnotManager.hideAnnotations(insertionMarkers);
      }
    }
  }

  private resolveIsColorDifference(difference: DifferenceType | string, id: string): boolean {
    if (typeof difference !== 'string') {
      return difference.type === DifferenceTypeNames.Color;
    }
    return getDifferences(store.getState()).find((d) => d.id === id)?.type === DifferenceTypeNames.Color;
  }

  /** Standard difference zoom (non-Color paths only; Color uses {@link getColorTargetZoomLevelOptions}). */
  private getZoomLevelOptionsForDifference(
    annotation: Core.Annotations.Annotation,
    viewer: WebViewerWrapper,
  ): { padding: number; upperBound: number } {
    const documentViewer = viewer.instance.Core.documentViewer;
    const viewElement = documentViewer.getScrollViewElement();
    const padding = this.calculateDynamicPadding(annotation, viewElement.clientWidth, viewElement.clientHeight, viewer);
    return { padding, upperBound: ZoomLevelLimits.differenceMax };
  }

  /** Target-only Color sample zoom (extra padding / cap; not used by dual or per-side fallback). */
  private getColorTargetZoomLevelOptions(
    annotation: Core.Annotations.Annotation,
    viewer: WebViewerWrapper,
  ): { padding: number; upperBound: number } {
    const base = this.getZoomLevelOptionsForDifference(annotation, viewer);
    return {
      padding: base.padding + COLOR_DIFFERENCE_ZOOM_EXTRA_PADDING_PX,
      upperBound: COLOR_DIFFERENCE_ZOOM_UPPER_BOUND,
    };
  }

  zoomToDifference(difference: DifferenceType | string): void {
    const { zoomLockLevel } = useZoomStateStore.getState();

    if (typeof difference !== 'string') {
      // For global font metrics results, fit to page instead of zooming to annotation
      if (difference.fontMetricsDetails?.isGlobalFontResult) {
        if (!zoomLockLevel[DocumentTypes.source]) {
          this.source?.fitToPage();
        }
        if (!zoomLockLevel[DocumentTypes.target]) {
          this.target?.fitToPage();
        }
        return;
      }
    }

    let id;
    if (typeof difference === 'string') {
      id = difference;
    } else {
      id = difference.id;
    }

    const isColorDifference = this.resolveIsColorDifference(difference, id);
    const sourceAnnotation = this.source?.getAnnotationById(id);
    const targetAnnotation = this.target?.getAnnotationById(id);

    // Color sample markers are target-only; hidden source annots are 0×0 — avoid dual/source zoom
    if (isColorDifference) {
      if (targetAnnotation && this.target) {
        const targetOpts = this.getColorTargetZoomLevelOptions(targetAnnotation, this.target);
        this.target.setDifferenceZoomLevel(targetAnnotation, !!zoomLockLevel[DocumentTypes.target], targetOpts);
      }
      return;
    }

    // Use dual zoom method when both annotations exist and neither is zoom locked
    if (
      sourceAnnotation &&
      targetAnnotation &&
      !zoomLockLevel[DocumentTypes.source] &&
      !zoomLockLevel[DocumentTypes.target] &&
      this.source &&
      this.target
    ) {
      this.setDualDifferenceZoomLevel(sourceAnnotation, targetAnnotation);
    } else {
      // Fall back to individual zoom for each annotation
      if (sourceAnnotation && this.source) {
        const sourceOpts = this.getZoomLevelOptionsForDifference(sourceAnnotation, this.source);
        this.source.setDifferenceZoomLevel(sourceAnnotation, !!zoomLockLevel[DocumentTypes.source], sourceOpts);
      }
      if (targetAnnotation && this.target) {
        const targetOpts = this.getZoomLevelOptionsForDifference(targetAnnotation, this.target);
        this.target.setDifferenceZoomLevel(targetAnnotation, !!zoomLockLevel[DocumentTypes.target], targetOpts);
      }
    }
  }

  calculateDynamicPadding(
    annotation: Core.Annotations.Annotation,
    viewerWidth: number,
    viewerHeight: number,
    viewer: WebViewerWrapper,
  ): number {
    const bounds = viewer.getAnnotationBounds(annotation);
    const rawFitX = viewerWidth / bounds.width;
    const rawFitY = viewerHeight / bounds.height;
    const usesXAxis = rawFitX <= rawFitY;

    // Use percentage of viewport for padding, adjusted based on how much of viewport the annotation takes
    const viewportSize = usesXAxis ? viewerWidth : viewerHeight;
    const annotationSize = usesXAxis ? bounds.width : bounds.height;
    const sizeRatio = annotationSize / viewportSize;

    // Exponential interpolation: larger annotations need less padding, smaller annotations need more
    // Uses power curve (exponent 1.3) to emphasize the difference between small and large annotations
    // At 0% of viewport → 45% padding (max)
    // At 100% of viewport → 5% padding (min)
    // At >100% of viewport → 5% padding (annotation larger than viewport)
    const maxPadding = 0.45;
    const minPadding = 0.05;
    let paddingRatio: number;

    if (sizeRatio >= 1.0) {
      // Annotation is larger than or equal to viewport - use minimal padding
      paddingRatio = minPadding;
    } else {
      // The exponent 1.3 was chosen empirically to provide a smooth transition:
      // - Higher exponents make padding drop off more sharply for larger annotations.
      // - Lower exponents make padding decrease more gradually.
      // 1.3 was found (via visual testing) to best balance padding for small vs. large annotations.
      paddingRatio = minPadding + (maxPadding - minPadding) * Math.pow(1 - sizeRatio, 1.3);
    }

    // Calculate initial padding
    let padding = viewportSize * paddingRatio;

    // SAFETY CHECK: Ensure padding works for BOTH dimensions
    // The padding is calculated based on one dimension, but must work for both
    const otherViewportSize = usesXAxis ? viewerHeight : viewerWidth;

    // Calculate what percentage of the other dimension would be consumed by padding
    const paddingPercentageOfOther = (2 * padding) / otherViewportSize;

    // If padding would consume too much of the other dimension (>70%), cap it more aggressively
    const maxAllowedPercentage = 0.7; // Padding can use max 70% of viewport (35% each side)
    if (paddingPercentageOfOther > maxAllowedPercentage) {
      const maxSafePadding = (otherViewportSize * maxAllowedPercentage) / 2;
      padding = maxSafePadding;
    }

    return padding;
  }

  private setDualDifferenceZoomLevel(
    sourceAnnotation: Core.Annotations.Annotation,
    targetAnnotation: Core.Annotations.Annotation,
  ): void {
    if (!this.source || !this.target) {
      return;
    }

    const sourceOpts = this.getZoomLevelOptionsForDifference(sourceAnnotation, this.source);
    this.source.setDifferenceZoomLevel(sourceAnnotation, false, sourceOpts);

    const targetOpts = this.getZoomLevelOptionsForDifference(targetAnnotation, this.target);
    this.target.setDifferenceZoomLevel(targetAnnotation, false, targetOpts);
  }

  scrollToDifferences(difference: DifferenceType | string): void {
    if (typeof difference !== 'string') {
      if (difference.fontMetricsDetails?.isGlobalFontResult) {
        this.source?.instance.Core.documentViewer.setCurrentPage(1, true);
        this.target?.instance.Core.documentViewer.setCurrentPage(1, true);
        return;
      }
    }

    let id;
    if (typeof difference === 'string') {
      id = difference;
    } else {
      id = difference.id;
    }
    if (id) {
      const sourceAnnot = this.source?.getAnnotationById(id);
      const targetAnnot = this.target?.getAnnotationById(id);
      const isColorDifference = this.resolveIsColorDifference(difference, id);

      if (sourceAnnot && !isColorDifference) {
        this.source?.centerOnAnnotation(sourceAnnot);
      }
      if (targetAnnot) {
        this.target?.centerOnAnnotation(targetAnnot);
      }

      const scrollElementSource = PDFTronManager.getScrollElement(DocumentTypes.source);
      const scrollElementTarget = PDFTronManager.getScrollElement(DocumentTypes.target);
      updatePreviousScrollPostion(scrollElementSource, scrollElementTarget);
    }
  }

  getPreviousDifferencePopup(previousDifference: DifferenceType) {
    const docManager = PDFManagerFactory.getPDFDocManager();
    const annotManager = PDFManagerFactory.getViewer(DocumentTypes.target)?.annotationManager;
    const renderPreviousDifferencePopup = () => {
      const button = document.createElement('button');
      button.classList.add('Button', 'ActionButton');

      const icon = document.createElement('img');
      icon.src = '/icons/leftChevron.svg';
      button.appendChild(icon);

      button.addEventListener('click', () => {
        const annotation = annotManager?.getAnnotationById(previousDifference.id);
        if (annotation) {
          annotManager?.deselectAllAnnotations();
          docManager?.zoomToDifference(previousDifference);
          docManager?.scrollToDifferences(previousDifference);
          annotManager?.selectAnnotation(annotation);
        }
      });

      return button;
    };
    return {
      type: 'customElement',
      title: 'Previous Difference',
      render: renderPreviousDifferencePopup,
    };
  }

  getNextDifferencePopup(nextDifference: DifferenceType) {
    const docManager = PDFManagerFactory.getPDFDocManager();
    const annotManager = PDFManagerFactory.getViewer(DocumentTypes.target)?.annotationManager;
    const renderNextDifferencePopup = () => {
      const button = document.createElement('button');
      button.classList.add('Button', 'ActionButton');

      const icon = document.createElement('img');
      icon.src = '/icons/rightChevron.svg';
      button.appendChild(icon);

      button.addEventListener('click', () => {
        const annotation = annotManager?.getAnnotationById(nextDifference.id);
        if (annotation) {
          annotManager?.deselectAllAnnotations();
          docManager?.zoomToDifference(nextDifference);
          docManager?.scrollToDifferences(nextDifference);
          annotManager?.selectAnnotation(annotation);
        }
      });

      return button;
    };
    return {
      type: 'customElement',
      title: 'Next Difference',
      render: renderNextDifferencePopup,
    };
  }

  public static createDifferenceAnnotation(instance: WebViewerInstance, documentType: DocumentType) {
    class DifferenceAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      private previousTextLocation?: AnnotationData['previousTextLocation'];

      private nextTextLocation?: AnnotationData['nextTextLocation'];

      private differenceType: DifferenceTypeNames;

      private readingOrderUxV2: boolean;

      constructor(data: {
        annotData: DiffToAnnotData;
        differenceId: string;
        type: string;
        groupId?: string;
        textHighlights?: any[];
        embeddedImageDetails?: any;
        matchIndex?: number;
        barcodeDetails?: any;
      }) {
        super();
        this.differenceType = data.type as DifferenceTypeNames;
        this.readingOrderUxV2 = getCachedFlagValue('reading_order_ux_v2');
        if (this.readingOrderUxV2) {
          this.previousTextLocation = data.annotData.previousTextLocation;
          this.nextTextLocation = data.annotData.nextTextLocation;
          this.setReadingOrderContextBounds(data.annotData);
        }

        if (data.type === DifferenceTypeNames.Markup) {
          this.ToolName = 'MarkupTool';
          this.setCustomData(MarkupZoneCustomData.markupZoneAnnotation, 'true');
          this.setCustomData(AnnotationCustomData.drawMarkups, 'true');
        }

        if (data.textHighlights) {
          // if we need to draw textfragment annotations as part of this difference, store it here to draw
          this.setCustomData(AnnotationCustomData.textHighlights, JSON.stringify(data.textHighlights));
        }

        if (data.matchIndex !== undefined && data.matchIndex !== null) {
          this.setCustomData(AnnotationCustomData.matchIndex, data.matchIndex.toString());
        }

        const annotationVisibility = Difference.differenceAnnotationVisibility(
          data.type as DifferenceTypeNames,
          documentType,
          data.embeddedImageDetails,
          data.annotData.location.rect,
        );

        if (
          annotationVisibility === AnnotationVisibilityOptions.visible ||
          annotationVisibility === AnnotationVisibilityOptions.placeholder
        ) {
          let newCoordinates: number[] | null = data.annotData.location.rect;
          if (newCoordinates) {
            this.Id = data.differenceId;
            this.PageNumber = Math.min(data.annotData.location.pageNumber, instance.Core.documentViewer.getPageCount()); // annotation page cannot be greater than the total page count
            this.ReadOnly = true;

            const norm = normalizeAnnotationRect(newCoordinates, Utils.annotationOptions.unselectedRectangeGrowthValue);
            this.X = norm.x;
            this.Y = norm.y;
            this.Width = norm.w;
            this.Height = norm.h;
            if (data.type === DifferenceTypeNames.Color && norm.w > 0 && norm.h > 0) {
              const cx = norm.x + norm.w / 2;
              const cy = norm.y + norm.h / 2;
              const half = COLOR_POINT_ANNOTATION_SIZE / 2;
              this.X = cx - half;
              this.Y = cy - half;
              this.Width = COLOR_POINT_ANNOTATION_SIZE;
              this.Height = COLOR_POINT_ANNOTATION_SIZE;
            }
            this.setCustomData(AnnotationCustomData.difference, 'true');
            instance.Core.Annotations.SelectionModel.defaultSelectionOutlineColor = new instance.Core.Annotations.Color(
              0,
              0,
              255,
            );
            if (data.groupId) {
              this.setCustomData('groupId', data.groupId);
            }
            // Store barcode grade and passingStatus for efficient color lookup during draw
            if (data.type === DifferenceTypeNames.Barcode) {
              const gradeMetadata = data.barcodeDetails?.barcodeMetadata?.find((m: any) => m.key === 'grade');
              // Always set barcodeGrade for barcode differences
              // Use the grade value if it exists, otherwise use 'NO_GRADE' as a sentinel
              const gradeValue = gradeMetadata?.value || 'NO_GRADE';
              this.setCustomData('barcodeGrade', gradeValue);
              // Store passingStatus if available
              if (gradeMetadata?.passingStatus) {
                this.setCustomData('barcodePassingStatus', gradeMetadata.passingStatus);
              }
            }
            if (annotationVisibility === AnnotationVisibilityOptions.placeholder) {
              // placeholder annotations are redrawn elsewhere so the orginial annotation rectangle must be hidden to avoid duplicates
              this.Hidden = true;
            }
          }
        } else if (annotationVisibility === AnnotationVisibilityOptions.hidden) {
          this.Id = data.differenceId;
          this.PageNumber = Math.min(data.annotData.location.pageNumber, instance.Core.documentViewer.getPageCount()); // annotation page cannot be greater than the total page count
          this.ReadOnly = true;
          if ((data.type as DifferenceTypeNames) === DifferenceTypeNames.Insertion) {
            let newCoordinates: number[] | null = data.annotData.location.rect;
            const norm = normalizeAnnotationRect(newCoordinates, Utils.annotationOptions.unselectedRectangeGrowthValue);
            this.X = norm.x;
            this.Y = norm.y;
            this.Width = norm.w;
            this.Height = norm.h;
          } else {
            this.X = 0;
            this.Y = 0;
            this.Width = 0;
            this.Height = 0;
          }
          this.setCustomData(AnnotationCustomData.difference, 'true');
          this.NoView = true;
        }
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        const realPageRotation = getPageRotation(instance, this.PageNumber);
        this.setStyles(ctx, pageMatrix);
        if (!instance) return;
        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        if (!pdfDocManager) return;
        const state = store.getState();
        const showDifferenceBoxes = getShowDifferenceBoxes(state);

        const { SELECTED_FILL_STYLE, DEFAULT_FILL_STYLE } = PREP_TOOL_ANNOTATION;

        const isResyncSelected = this.getCustomData('resyncSelected') === 'true';
        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        const isSelected = selectedAnnotations.some((targetAnnot) => targetAnnot.Id === this.Id);

        if (this.differenceType === DifferenceTypeNames.Color) {
          ctx.fillStyle = 'rgba(0, 0, 0, 0.01)';
          ctx.fillRect(this.X, this.Y, this.Width, this.Height);
          const borderStroke = isResyncSelected
            ? '#02A4B6'
            : showDifferenceBoxes
              ? SELECTED_FILL_STYLE
              : 'rgba(255, 255, 255, 0.01)';
          ctx.strokeStyle = borderStroke;
          ctx.lineWidth = 1;
          ctx.globalCompositeOperation = 'source-over';
          ctx.strokeRect(this.X, this.Y, this.Width, this.Height);

          const cx = this.X + this.Width / 2;
          const cy = this.Y + this.Height / 2;
          const dotFill = isResyncSelected
            ? '#02A4B6'
            : showDifferenceBoxes
              ? SELECTED_FILL_STYLE
              : 'rgba(255, 255, 255, 0.01)';
          ctx.fillStyle = dotFill;
          ctx.fillRect(cx - 1, cy - 1, 2, 2);

          if (showDifferenceBoxes && (isSelected || (this as any)?.IsHovering)) {
            const colorDiff = getDifferences(state).find((d) => d.id === this.Id);
            const n = (colorDiff?.differenceIndex ?? 0) + 1;
            const labelText = `Color Point #${n}`;
            const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
            const pgWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
            const pgHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);
            drawMarkupLabel(
              ctx,
              labelText,
              this.X,
              this.Y,
              HEIGHT,
              GAP,
              WIDTH_PADDING,
              isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE,
              zoomLevel,
              false,
              this.Width,
              this.Height,
              realPageRotation,
              pgWidth,
              pgHeight,
            );
          }

          return;
        }

        // Determine stroke color - use grade color for barcodes
        let strokeColor = SELECTED_FILL_STYLE; // default red
        const barcodeGrade = this.getCustomData('barcodeGrade');
        const barcodePassingStatus = this.getCustomData('barcodePassingStatus');
        // Only apply barcode colors if this is actually a barcode (has barcodeGrade custom data set)
        // PDFTron's getCustomData may return empty string for non-existent keys
        // getBarcodeGradeColor handles invalid grades (like 'NO_GRADE') by returning green
        if (barcodeGrade && barcodeGrade !== '') {
          strokeColor = getBarcodeGradeColor(barcodeGrade, barcodePassingStatus);
        }

        // Draw annotation rectangle with custom styling
        const annotationRectangle = new Path2D();
        annotationRectangle.rect(this.X, this.Y, this.Width, this.Height);
        // If selected for resync, override stroke to blue; otherwise use standard display logic
        const resolvedStrokeColor = isResyncSelected
          ? '#02A4B6'
          : showDifferenceBoxes
            ? strokeColor
            : 'rgba(255, 255, 255, 0.01)'; // transparent but clickable
        ctx.strokeStyle = resolvedStrokeColor;
        ctx.globalCompositeOperation = 'source-over';
        ctx.stroke(annotationRectangle);
        const groupId = this.getCustomData('groupId');
        if (isSelected) {
          const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
          const pgWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pgHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);

          // Draws the member label and the prev/next popup for a grouped difference. Called with
          // the store's numbering when available, otherwise with the legacy getGroupedDifferences data.
          const drawGroupLabelAndPopup = (
            labelText: string,
            previousDifference: DifferenceType,
            nextDifference: DifferenceType,
            representativeType: DifferenceTypeNames,
          ) => {
            drawMarkupLabel(
              ctx,
              labelText,
              this.X,
              this.Y,
              HEIGHT,
              GAP,
              WIDTH_PADDING,
              SELECTED_FILL_STYLE,
              zoomLevel,
              true,
              this.Width,
              this.Height,
              realPageRotation,
              pgWidth,
              pgHeight,
            );
            instance.Core.annotationManager.bringToFront(this); // required to make markup label in front of overlay annotation

            // For deletion differences, show popup on source; for others, show on target
            const shouldShowPopup =
              (representativeType === DifferenceTypeNames.Deletion && documentType === DocumentTypes.source) ||
              (representativeType !== DifferenceTypeNames.Deletion && documentType === DocumentTypes.target);

            if (shouldShowPopup) {
              const prevDiffButton = pdfDocManager.getPreviousDifferencePopup(previousDifference);
              const nextDiffButton = pdfDocManager.getNextDifferencePopup(nextDifference);
              instance.UI.annotationPopup.update([prevDiffButton, nextDiffButton]);
              // WebViewer only opens the popup when its focused annotation changes, and it skips the
              // open when the popup has no items yet — which is always the case here, since draw()
              // runs after selection. Open it explicitly now that the buttons exist.
              instance.UI.openElements(['annotationPopup']);
            }
          };

          // Prefer the numbering the store already computed (N.k). getGroupLabelFromStore returns
          // null for ungrouped diffs and when the store has no entry (All tab / not yet computed /
          // imported VerifyAPI inspections), in which case we fall back to the legacy k/size.
          const storeMembers = useDifferenceListStore.getState().groupMembersByDifferenceId[this.Id];
          const storeLabel = getGroupLabelFromStore(storeMembers, this.Id);
          if (storeLabel) {
            drawGroupLabelAndPopup(
              storeLabel.labelText,
              storeLabel.previous,
              storeLabel.next,
              storeLabel.representative.type,
            );
          } else if (groupId) {
            const differencesInGroup = getGroupedDifferences(groupId)(store.getState());
            const groupSize = differencesInGroup.length;
            if (groupSize > 1) {
              const differenceIndex = differencesInGroup.findIndex((diff) => diff.id === this.Id);
              const previousDifference = differencesInGroup[(differenceIndex + groupSize - 1) % groupSize];
              const nextDifference = differencesInGroup[(differenceIndex + 1) % groupSize];
              drawGroupLabelAndPopup(
                `${differenceIndex + 1}/${groupSize}`,
                previousDifference,
                nextDifference,
                differencesInGroup[0].type,
              );
            }
          }

          if (this.differenceType === DifferenceTypeNames.ReadingOrder && this.readingOrderUxV2) {
            this.drawReadingOrderContextBoxes(ctx, showDifferenceBoxes, zoomLevel, realPageRotation);
          }
        }
      }
      private setReadingOrderContextBounds(annotData: DiffToAnnotData) {
        if (this.differenceType !== DifferenceTypeNames.ReadingOrder) {
          return;
        }
        const baseLocation = annotData.location;
        const growthPadding = Utils.annotationOptions.unselectedRectangeGrowthValue;
        if (!baseLocation?.rect) return;
        const basePage = baseLocation.pageNumber;

        const rects: number[][] = [baseLocation.rect];

        const addContextRect = (location?: AnnotationData['previousTextLocation']) => {
          if (!location || location.pageNumber !== basePage || !location.rect) {
            return;
          }
          rects.push(location.rect);
        };

        addContextRect(this.previousTextLocation);
        addContextRect(this.nextTextLocation);

        if (rects.length <= 1) {
          return;
        }

        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        rects.forEach((rect) => {
          const [x = 0, y = 0, width = 0, height = 0] = rect;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x + width);
          maxY = Math.max(maxY, y + height);
        });

        if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
          return;
        }

        const contextBounds = {
          x: minX - growthPadding,
          y: minY - growthPadding,
          width: maxX - minX + 2 * growthPadding,
          height: maxY - minY + 2 * growthPadding,
        };

        this.setCustomData('readingOrderContextBounds', JSON.stringify(contextBounds));
      }

      private drawReadingOrderContextBoxes(
        ctx: CanvasRenderingContext2D,
        showDifferenceBoxes: boolean,
        zoomLevel: number,
        pageRotation: number,
      ) {
        const contextLocations = [
          { location: this.previousTextLocation, label: 'before' as const },
          { location: this.nextTextLocation, label: 'after' as const },
        ];

        const padding = Utils.annotationOptions.unselectedRectangeGrowthValue;
        const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
        const { SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
        const readingOrderColor = differenceHighlightColors[DifferenceTypeNames.ReadingOrder] || SELECTED_FILL_STYLE;

        contextLocations.forEach(({ location, label }) => {
          if (!location || location.pageNumber !== this.PageNumber) {
            return;
          }
          const [rectX = 0, rectY = 0, rectWidth = 0, rectHeight = 0] = location.rect || [];
          const x = rectX - padding;
          const y = rectY - padding;
          const width = rectWidth + 2 * padding;
          const height = rectHeight + 2 * padding;

          const contextRectangle = new Path2D();
          contextRectangle.rect(x, y, width, height);
          ctx.strokeStyle = showDifferenceBoxes ? readingOrderColor : 'rgba(255, 255, 255, 0.01)';
          ctx.globalCompositeOperation = 'source-over';
          ctx.stroke(contextRectangle);

          if (showDifferenceBoxes) {
            const pgW = instance.Core.documentViewer.getPageWidth(this.PageNumber);
            const pgH = instance.Core.documentViewer.getPageHeight(this.PageNumber);
            drawMarkupLabel(
              ctx,
              label,
              x,
              y,
              HEIGHT,
              GAP,
              WIDTH_PADDING,
              readingOrderColor,
              zoomLevel,
              false,
              width,
              height,
              pageRotation,
              pgW,
              pgH,
            );
          }
        });
      }
    }
    return DifferenceAnnotation;
  }

  public static createMissingContentMarkerAnnotation(instance: WebViewerInstance) {
    class MissingContentMarkerAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      constructor(data: {
        differenceId: string;
        position: number[];
        pageNumber: number;
        groupId?: string;
        differenceType: DifferenceTypeNames;
      }) {
        super();

        this.Id = data.differenceId;
        this.PageNumber = Math.min(data.pageNumber, instance.Core.documentViewer.getPageCount());
        this.X = data.position[0];
        this.Y = data.position[1];
        this.Width = 30;
        this.Height = 30;

        // Marker properties - for missing content (deletions on target, insertions on source)
        this.Hidden = true; // Hidden by default - shown only when focused
        this.ReadOnly = false; // Movable by user
        this.NoResize = true; // Not resizable
        this.setCustomData(AnnotationCustomData.deletionMarker, 'true'); // Keep for backward compatibility
        this.setCustomData(AnnotationCustomData.difference, 'true');
        this.setCustomData('differenceType', data.differenceType);

        if (data.groupId) {
          this.setCustomData('groupId', data.groupId);
        }
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        const realPageRotation = getPageRotation(instance, this.PageNumber);
        this.setStyles(ctx, pageMatrix);
        if (!instance) return;

        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        if (!pdfDocManager) return;

        // Draw X shape marker
        ctx.globalAlpha = 1;
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#cd3434'; // transparent but clickable

        // Calculate center point for X
        const centerX = this.X + this.Width / 2;
        const centerY = this.Y + this.Height / 2;

        ctx.beginPath();
        ctx.moveTo(centerX - 10, centerY - 10);
        ctx.lineTo(centerX + 10, centerY + 10);
        ctx.moveTo(centerX + 10, centerY - 10);
        ctx.lineTo(centerX - 10, centerY + 10);
        ctx.stroke();
        ctx.closePath();

        // Handle group labeling similar to regular differences
        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        const isSelected = selectedAnnotations.some((targetAnnot) => targetAnnot.Id === this.Id);
        const groupId = this.getCustomData('groupId');

        if (isSelected) {
          // Prefer the store's numbering (N.k); fall back to legacy k/size when the store has no
          // entry (All tab / not yet computed / imported VerifyAPI inspections). Markers show the
          // label only — no prev/next popup.
          const storeMembers = useDifferenceListStore.getState().groupMembersByDifferenceId[this.Id];
          const storeLabel = getGroupLabelFromStore(storeMembers, this.Id);
          let labelText: string | null = storeLabel?.labelText ?? null;
          if (!labelText && groupId) {
            const differencesInGroup = getGroupedDifferences(groupId)(store.getState());
            const groupSize = differencesInGroup.length;
            if (groupSize > 1) {
              const differenceIndex = differencesInGroup.findIndex((diff) => diff.id === this.Id);
              labelText = `${differenceIndex + 1}/${groupSize}`;
            }
          }

          if (labelText) {
            const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
            const { SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
            const mcPgW = instance.Core.documentViewer.getPageWidth(this.PageNumber);
            const mcPgH = instance.Core.documentViewer.getPageHeight(this.PageNumber);
            drawMarkupLabel(
              ctx,
              labelText,
              this.X,
              this.Y,
              HEIGHT,
              GAP,
              WIDTH_PADDING,
              SELECTED_FILL_STYLE,
              zoomLevel,
              false,
              this.Width,
              this.Height,
              realPageRotation,
              mcPgW,
              mcPgH,
            );
            instance.Core.annotationManager.bringToFront(this);
          }
        }
      }
    }
    return MissingContentMarkerAnnotation;
  }

  public static drawTextHighlights(
    differences: DifferenceType[],
    instance: WebViewerInstance,
    document: DocumentType,
  ): void {
    const state = store.getState();
    // Only draw text highlights if enabled AND we're not on Prep
    const isPrepTab = !isResultUrl() && !isReportsUrl();
    if (!state.app.showTextHighlights || isPrepTab) return;

    // The focused difference is shown via its own focus/overlay pipeline, not this generic
    // batch. We must still (re)create highlights for every other displayed difference so that
    // re-enabling a filter while a difference is focused brings their highlights back.
    const focusedDifferenceId = state.inspection.focusedDifferenceId;

    // Create text highlight annotations for each difference
    const textHighlightAnnotations: Core.Annotations.Annotation[] = [];
    differences.forEach((difference) => {
      if (difference.id === focusedDifferenceId) return;
      // Text Highlights (VE-10556) we need to add highlights to text differences.
      // 1. check if difference is a text difference. if it is, we need to add textHighlights to the annotation.
      // 2. if textFragments IS present, we highlight using coordinates of textFragments.
      // 3. if textFragments IS NOT present, we highlight using bounding box coordinates instead.
      let textHighlights = undefined;
      if (
        difference.type === DifferenceTypeNames.UnmatchedAnnotation ||
        (Difference.requiresDeletionMarker([difference]) && document === DocumentTypes.target) ||
        ((difference.type === DifferenceTypeNames.Insertion || difference.type in SpellingDifferences) &&
          document === DocumentTypes.source)
      ) {
        return;
      }

      if (
        (difference.type in ContentDifferences ||
          difference.type in FormattingDifferences ||
          difference.type in SpellingDifferences) &&
        difference.type !== DifferenceTypeNames.EmbeddedImage
      ) {
        // check if highlight already exists
        const existingHighlight = instance.Core.annotationManager.getAnnotationById(`${difference.id}-text-highlight`);
        if (existingHighlight) {
          return;
        }

        if (difference[document].textFragments) {
          textHighlights = difference[document].textFragments;
        } else {
          // populate textHighlights with bounding box coordinates
          textHighlights = [
            {
              pageNumber: difference[document].location.pageNumber,
              rect: difference[document].location.rect,
            },
          ];
        }
        // create a text highlight annotation for each difference containing all the textFragments in the customData
        const TextHighlightAnnotClass = Difference.createTextHighlightAnnotation(instance);
        const textHighlightAnnot = new TextHighlightAnnotClass({
          rect: difference[document].location.rect,
          textFragments: JSON.stringify(textHighlights),
          pageNumber: difference[document].location.pageNumber,
          parentId: difference.id,
          id: `${difference.id}-text-highlight`,
          differenceType: difference.type as DifferenceTypeNames,
        });
        textHighlightAnnotations.push(textHighlightAnnot);
      }
    });
    if (textHighlightAnnotations.length > 0) {
      const annotationManager = instance.Core.annotationManager;
      annotationManager.addAnnotations(textHighlightAnnotations);

      PDFAnnotationManager.bringInternalAnnotationsToFront(document);

      instance.Core.annotationManager.redrawAnnotation(textHighlightAnnotations[0]);
    }
  }

  private static createTextHighlightAnnotation(instance: WebViewerInstance) {
    class DifferenceTextHighlightAnnotation extends GVAnnotationMixin(
      instance.Core.Annotations.TextHighlightAnnotation,
    ) {
      constructor(data: {
        rect: number[];
        textFragments: string;
        pageNumber: number;
        parentId: string;
        id: string;
        differenceType: DifferenceTypeNames;
      }) {
        super();

        this.Id = data.id;
        this.PageNumber = data.pageNumber;
        const norm = normalizeAnnotationRect(data.rect, Utils.annotationOptions.unselectedRectangeGrowthValue);
        this.X = norm.x;
        this.Y = norm.y;
        this.Width = norm.w;
        this.Height = norm.h;
        this.setCustomData('parentId', data.parentId);
        this.setCustomData('isTextHighlight', 'true');
        this.setCustomData(AnnotationCustomData.textHighlights, data.textFragments);
        this.setCustomData('differenceType', data.differenceType);
        this.ReadOnly = true;
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        const textHighlights = this.getCustomData(AnnotationCustomData.textHighlights);
        const differenceType = this.getCustomData('differenceType') as DifferenceTypeNames;
        // get selected highlight color from store
        const selectedHighlightColor = store.getState().inspection.settings.highlightColor;

        // Get base color without alpha
        let hexColor = differenceHighlightColors[differenceType] || DEFAULT_DIFFERENCE_COLOR; // Default to a solid hex color (e.g., the deletion red)
        if (selectedHighlightColor && selectedHighlightColor !== ColorId.MULTICOLOR) {
          hexColor =
            colorIdToHex[selectedHighlightColor as Exclude<ColorId, ColorId.MULTICOLOR>] || DEFAULT_DIFFERENCE_COLOR;
        }

        // If parent difference is selected for resync, override to blue to match zone selection
        const parentId = this.getCustomData('parentId');
        if (parentId) {
          const parentAnnot = instance.Core.annotationManager.getAnnotationById(parentId);
          const isResyncSelected = parentAnnot?.getCustomData('resyncSelected') === 'true';
          if (isResyncSelected) {
            hexColor = '#00E4F5';
          }
        }

        const textFragmentsData = JSON.parse(textHighlights);
        if (textFragmentsData.length > 0) {
          // Set fill style to the base hex color
          ctx.fillStyle = hexColor;

          // Determine and set global alpha
          ctx.globalAlpha = differenceType in FormattingDifferences ? 0.15 : 0.2; // the pink is 15% and the other colors are 20%

          ctx.globalCompositeOperation = 'destination-over';
          textFragmentsData.forEach((textFragment: any) => {
            const fragmentCoordinates = textFragment.rect;
            const x = fragmentCoordinates[0] - Utils.annotationOptions.unselectedRectangeGrowthValue;
            const y = fragmentCoordinates[1] - Utils.annotationOptions.unselectedRectangeGrowthValue;
            const width = fragmentCoordinates[2] + 2 * Utils.annotationOptions.unselectedRectangeGrowthValue;
            const height = fragmentCoordinates[3] + 2 * Utils.annotationOptions.unselectedRectangeGrowthValue;
            const textFragmentRectangle = new Path2D();
            textFragmentRectangle.rect(x, y, width, height);
            ctx.clearRect(x, y, width, height);
            ctx.fill(textFragmentRectangle);
          });

          // Reset globalAlpha to avoid affecting other drawing operations
          ctx.globalAlpha = 1.0;
        }
      }
    }

    return DifferenceTextHighlightAnnotation;
  }

  public static isGraphic(differences: DifferenceType[]): boolean {
    return differences.some((difference) => difference.type === DifferenceTypeNames.Graphics);
  }

  public static isGraphicOrEmbeddedImage(differences: DifferenceType[]): boolean {
    return differences.some(
      (difference) =>
        difference.type === DifferenceTypeNames.Graphics || difference.type === DifferenceTypeNames.EmbeddedImage,
    );
  }

  public static requiresDeletionMarker(differences: DifferenceType[]): boolean {
    return differences.some(
      (difference) =>
        difference.type === DifferenceTypeNames.Deletion ||
        difference.type === DifferenceTypeNames.UnmatchedAnnotation ||
        (difference.type === DifferenceTypeNames.EmbeddedImage &&
          'embeddedImageDetails' in difference &&
          difference.embeddedImageDetails?.matchDetails[0]?.confidenceLevel === 'None' &&
          !difference.embeddedImageDetails?.userLinkedMatch),
    );
  }

  /**
   * Which document had the embedded-image deletion marker (user-drawn match target), from API flag or
   * coordinate fallback for older differences. Does not check confidence / userLinkedMatch — use for UI
   * that needs the side even after a link (e.g. thumbnailPaths).
   */
  public static getEmbeddedImageDeletionMarkerSide(difference: DifferenceType): 'source' | 'target' | null {
    if (difference.type !== DifferenceTypeNames.EmbeddedImage || !('embeddedImageDetails' in difference)) {
      return null;
    }

    const deletionMarkerTarget = difference.embeddedImageDetails?.deletionMarkerTarget;
    if (deletionMarkerTarget === 'source' || deletionMarkerTarget === 'target') {
      return deletionMarkerTarget;
    }

    // Fallback: Check coordinates for backward compatibility with existing differences
    const sourceRect = difference.source?.location?.rect || [0, 0, 0, 0];
    const sourceIsEmpty = sourceRect.every((coord) => coord === 0);

    const targetRect = difference.target?.location?.rect || [0, 0, 0, 0];
    const targetIsEmpty = targetRect.every((coord) => coord === 0);

    if (sourceIsEmpty && !targetIsEmpty) {
      return 'source';
    }
    if (targetIsEmpty && !sourceIsEmpty) {
      return 'target';
    }

    // After user link both rects may be non-empty; uploaded thumbnail is only written to one thumbnailPaths slot
    const srcUrl = difference.thumbnailPaths?.source?.thumbnailUrl;
    const tgtUrl = difference.thumbnailPaths?.target?.thumbnailUrl;
    if (typeof srcUrl === 'string' && srcUrl.length > 0 && tgtUrl === undefined) {
      return 'source';
    }
    if (typeof tgtUrl === 'string' && tgtUrl.length > 0 && srcUrl === undefined) {
      return 'target';
    }

    return null;
  }

  public static getEmbeddedImageDeletionTarget(difference: DifferenceType): 'source' | 'target' | null {
    if (difference.type !== DifferenceTypeNames.EmbeddedImage || !('embeddedImageDetails' in difference)) {
      return null;
    }

    if (difference.embeddedImageDetails?.userLinkedMatch) {
      return null;
    }

    const matchDetails = difference.embeddedImageDetails?.matchDetails[0];
    if (!matchDetails || matchDetails.confidenceLevel !== 'None') {
      return null;
    }

    return Difference.getEmbeddedImageDeletionMarkerSide(difference);
  }

  public static isInsertion(differences: DifferenceType[]): boolean {
    return differences.some((difference: DifferenceType) => difference.type === DifferenceTypeNames.Insertion);
  }

  public static isSpelling(differences: DifferenceType[]): boolean {
    const spellingTypes = [
      DifferenceTypeNames.SpellingGeneral,
      DifferenceTypeNames.Abbreviation,
      DifferenceTypeNames.ProperNoun,
      DifferenceTypeNames.Alphanumeric,
    ];
    return differences.some((difference: DifferenceType) => spellingTypes.includes(difference.type));
  }

  public static hasGrid(differences: DifferenceType[], document: DocumentTypes): boolean {
    return differences.some((difference: DifferenceType) => difference[document] && difference[document]?.text);
  }

  public static isBarcode(differences: DifferenceType[]): boolean {
    return differences.some((difference) => difference.type === DifferenceTypeNames.Barcode);
  }

  public static isUnmatchedAnnotation(differences: DifferenceType[]): boolean {
    return differences.some((difference) => difference.type === DifferenceTypeNames.UnmatchedAnnotation);
  }

  /* although not every difference type has a pair of visible annotations (ex. deletion differences do not have a visible target annotation),
  each difference MUST have a pair of source/target annotations for the purposes of:
    1. difference selection/focus logic
    2. annotation overlay logic
  The purpose of this function is to outline which difference annotations are for the purpose to exist but to be hidden
  */
  static differenceAnnotationVisibility(
    differenceType: DifferenceTypeNames,
    documentType: DocumentTypes,
    embeddedImageDetails?: any,
    locationRect?: number[],
  ): AnnotationVisibilityOptions {
    const hiddenSourceAnnotations = [
      DifferenceTypeNames.SpellingGeneral,
      DifferenceTypeNames.Abbreviation,
      DifferenceTypeNames.ProperNoun,
      DifferenceTypeNames.Alphanumeric,
      DifferenceTypeNames.Barcode,
      DifferenceTypeNames.Braille,
      DifferenceTypeNames.FontMetrics,
    ];
    if (documentType === DocumentTypes.source && hiddenSourceAnnotations.includes(differenceType))
      return AnnotationVisibilityOptions.hidden;

    // Color sample points are target-only; never show the marker on source
    if (documentType === DocumentTypes.source && differenceType === DifferenceTypeNames.Color) {
      return AnnotationVisibilityOptions.hidden;
    }

    // Insertions on source document show placeholder marker (like deletions on target)
    if (documentType === DocumentTypes.source && differenceType === DifferenceTypeNames.Insertion) {
      return AnnotationVisibilityOptions.placeholder;
    }

    // Handle unmatched embedded images - show placeholder ONLY on the document where the deletion marker is located
    // The deletion marker overlay handles displaying the marker on the correct document
    if (
      differenceType === DifferenceTypeNames.EmbeddedImage &&
      embeddedImageDetails?.matchDetails?.[0]?.confidenceLevel === 'None' &&
      !embeddedImageDetails?.userLinkedMatch
    ) {
      // Use deletionMarkerTarget flag if available (set when difference is created)
      const deletionMarkerTarget = embeddedImageDetails?.deletionMarkerTarget;
      if (deletionMarkerTarget === 'source' && documentType === DocumentTypes.source) {
        // Deletion marker is on source document - return placeholder
        return AnnotationVisibilityOptions.placeholder;
      } else if (deletionMarkerTarget === 'target' && documentType === DocumentTypes.target) {
        // Deletion marker is on target document - return placeholder
        return AnnotationVisibilityOptions.placeholder;
      } else if (deletionMarkerTarget) {
        // Deletion marker is on the other document - return visible
        return AnnotationVisibilityOptions.visible;
      }

      // Fallback: Check coordinates for backward compatibility with existing differences
      // Check if this document has zero coordinates (image missing) - if so, deletion marker should be here
      // If locationRect is provided, use it; otherwise check matchDetails
      if (locationRect) {
        const isEmpty = locationRect.every((coord) => coord === 0);
        if (isEmpty) {
          // Image is missing on this document, deletion marker should be here - return placeholder
          return AnnotationVisibilityOptions.placeholder;
        }
      } else {
        // Fallback: check matchDetails to determine which document has the marker
        const matchDetails = embeddedImageDetails?.matchDetails?.[0];
        if (matchDetails) {
          const masterLocation = matchDetails.masterLocation;
          const sampleLocation = matchDetails.sampleLocation;
          const masterIsEmpty =
            masterLocation?.boundingBox && Object.values(masterLocation.boundingBox).every((val) => val === 0);
          const sampleIsEmpty =
            sampleLocation?.boundingBox && Object.values(sampleLocation.boundingBox).every((val) => val === 0);

          if (documentType === DocumentTypes.source && masterIsEmpty && !sampleIsEmpty) {
            // Image exists in sample but not in master - deletion marker goes to source
            return AnnotationVisibilityOptions.placeholder;
          } else if (documentType === DocumentTypes.target && sampleIsEmpty && !masterIsEmpty) {
            // Image exists in master but not in sample - deletion marker goes to target
            return AnnotationVisibilityOptions.placeholder;
          }
        }
      }
      // Image exists on this document, deletion marker is on the other document - return visible
      return AnnotationVisibilityOptions.visible;
    }

    if (documentType === DocumentTypes.target) {
      if (
        differenceType === DifferenceTypeNames.Deletion ||
        differenceType === DifferenceTypeNames.UnmatchedAnnotation
      ) {
        return AnnotationVisibilityOptions.placeholder;
      }
    }
    return AnnotationVisibilityOptions.visible;
  }

  deleteDifferenceAnnotations() {
    const state = store.getState();
    const unfilteredDiffs = getUnfilteredDifferences(state);
    const source = PDFManagerFactory.getViewer(DocumentTypes.source);
    const target = PDFManagerFactory.getViewer(DocumentTypes.target);
    [source, target].forEach((instance) => {
      const instanceDiffAnnots: Core.Annotations.Annotation[] = [];
      unfilteredDiffs.forEach((diff) => {
        const annot = instance?.getAnnotationById(diff.id);
        if (annot) {
          instanceDiffAnnots.push(annot);
        }
      });
      if (instanceDiffAnnots) {
        instance?.annotationManager.deleteAnnotations(instanceDiffAnnots, { imported: true, force: true });
      }
    });
  }

  // This type is actually Difference[] from the types file but it had to be renamed as this is exporting Difference
  redrawDifferenceAnnotations(differences: DifferenceType[]) {
    // Draw text highlights as separate annotations
    PDFAnnotationManager.drawAllTexts(differences, DocumentTypes.source);
    PDFAnnotationManager.drawAllTexts(differences, DocumentTypes.target);
  }
}

export default Difference;
